import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  AppLayout,
  Box,
  Button,
  Header,
  Input,
  LiveRegion,
  SegmentedControl,
  Select,
  SelectProps,
  SpaceBetween,
  Table
} from '@amzn/awsui-components-core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { TableEmptyState, TableNoMatchState } from 'src/components/generic-components/TableCommons';
import { HomePageSideNavigationV2 } from 'src/components/side-navigation-v2/HomePageSideNavigationV2';
import { eUserPreferenceKeys } from 'src/constants/AppConstants';
import { usePersistedState } from 'src/hooks/useLocalStorage';
import { appLayoutAriaLabels } from 'src/i18n-strings';
import { getTextFilterCounterText } from 'src/i18n-strings/text-filter';
import { Titles } from '../das-finsuite-constants';
import { AllAppsGridView } from './AllAppsGridView';
import {
  COLUMN_DEFINITIONS,
  DEFAULT_MODULE,
  DEFAULT_PREFERENCES,
  DEFAULT_TEAM,
  FILTER_LABEL_MODULE,
  FILTER_LABEL_TEAM,
  SEARCHABLE_COLUMNS,
  getFilteredHeaderCounterText,
  matchesModule,
  matchesTeam,
  prepareSelectOptions
} from './all-apps-config';
import { useAllAppsFilterUrlSync } from './hooks/useAllAppsFilterUrlSync';

import 'src/styles/all-apps-table-select.scss';

type ViewMode = 'list' | 'grid';

const AllApps: React.FC = () => {
  const appContext = useAppContext();
  const navigate = useNavigate();
  const activeParsedApplications = appContext.parsedApplications.filter((app) => app.isActive);

  const [team, setTeam] = useState<SelectProps.Option>(DEFAULT_TEAM);
  const [module, setModule] = useState<SelectProps.Option>(DEFAULT_MODULE);
  const [preferences] = usePersistedState(eUserPreferenceKeys.ALL_APPS_TABLE_PREFERENCES, DEFAULT_PREFERENCES);
  const [viewMode, setViewMode] = usePersistedState<ViewMode>(eUserPreferenceKeys.ALL_APPS_VIEW_MODE, 'grid');

  const selectTeamOptions = useMemo(
    () => prepareSelectOptions(activeParsedApplications, 'applicationOwner', DEFAULT_TEAM),
    [activeParsedApplications]
  );
  const selectModuleOptions: SelectProps.Options = useMemo(
    () => [DEFAULT_MODULE, ...appContext.distinctModules.map((label, i) => ({ label, value: (i + 1).toString() }))],
    [appContext.distinctModules]
  );

  const isTeamFiltered = team.value !== DEFAULT_TEAM.value;
  const isModuleFiltered = module.value !== DEFAULT_MODULE.value;

  const { items, actions, filteredItemsCount, collectionProps, filterProps } = useCollection(activeParsedApplications, {
    filtering: {
      empty: <TableEmptyState resourceName="Applications" />,
      noMatch: <TableNoMatchState onClearFilter={() => clearFilter()} />,
      filteringFunction: (item, filteringText) => {
        if (!matchesTeam(item, team)) {
          return false;
        }
        if (!matchesModule(item, module)) {
          return false;
        }
        const filteringTextLowerCase = filteringText.toLowerCase();
        return SEARCHABLE_COLUMNS.map((key) => item[key]).some(
          (value) => typeof value === 'string' && value.toLowerCase().includes(filteringTextLowerCase)
        );
      }
    },
    sorting: { defaultState: { sortingColumn: COLUMN_DEFINITIONS[2] } },
    selection: {}
  });

  const isFiltered = isTeamFiltered || isModuleFiltered || filterProps.filteringText.length > 0;
  const headerCounter = getFilteredHeaderCounterText(filteredItemsCount ?? items.length, activeParsedApplications.length);

  const setFilteringText = useCallback((text: string) => actions.setFiltering(text), [actions]);

  useAllAppsFilterUrlSync({
    teamOptions: selectTeamOptions,
    moduleOptions: selectModuleOptions,
    team,
    module,
    filteringText: filterProps.filteringText,
    setTeam,
    setModule,
    setFilteringText
  });

  useEffect(() => {
    collectionProps.ref.current?.scrollToTop();
  }, [team, module, filterProps.filteringText]);

  const [isScrolled, setIsScrolled] = useState(false);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (viewMode !== 'grid' || !stickyHeaderRef.current) {
      setIsScrolled(false);
      return;
    }
    let target: HTMLElement | null = stickyHeaderRef.current.parentElement;
    while (target) {
      const { overflowY } = getComputedStyle(target);
      if (overflowY === 'auto' || overflowY === 'scroll') break;
      target = target.parentElement;
    }
    if (!target) {
      setIsScrolled(false);
      return;
    }
    const scrollTarget = target;
    setIsScrolled(scrollTarget.scrollTop > 0);
    const onScroll = () => setIsScrolled(scrollTarget.scrollTop > 0);
    scrollTarget.addEventListener('scroll', onScroll);
    return () => scrollTarget.removeEventListener('scroll', onScroll);
  }, [viewMode]);

  function clearFilter() {
    actions.setFiltering('');
    setTeam(DEFAULT_TEAM);
    setModule(DEFAULT_MODULE);
  }

  const headerContent = (
    <Box padding={{ top: 'm' }}>
      <Header
        variant="awsui-h1-sticky"
        counter={headerCounter}
        actions={
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <SegmentedControl
              selectedId={viewMode}
              onChange={({ detail }) => setViewMode(detail.selectedId as ViewMode)}
              options={[
                { id: 'list', iconName: 'list-view', text: '' },
                { id: 'grid', iconName: 'grid-view', text: '' }
              ]}
            />
            {appContext.isAdminControlsEnabled && (
              <>
                <Button variant="normal" onClick={() => navigate('/admin/applications')}>
                  Manage in Admin Console
                </Button>
                <Button iconName="add-plus" variant="primary" onClick={() => navigate('/admin/manage-applications')}>
                  New Application
                </Button>
              </>
            )}
          </SpaceBetween>
        }
      >
        {Titles.TITLE_ALL_APPS}
      </Header>
    </Box>
  );

  const filterContent = (
    <div className="input-container">
      <div className="input-filter">
        <Input
          type="search"
          value={filterProps.filteringText}
          onChange={(event) => actions.setFiltering(event.detail.value)}
          ariaLabel="Find applications"
          placeholder="Filter applications by text, property or value"
          clearAriaLabel="clear"
        />
      </div>
      <div className={`select-filter${isTeamFiltered ? ' select-filter--active' : ''}`}>
        <Select
          inlineLabelText={FILTER_LABEL_TEAM}
          options={selectTeamOptions}
          selectedAriaLabel="Selected"
          selectedOption={team}
          onChange={(event) => setTeam(event.detail.selectedOption)}
          expandToViewport={true}
        />
      </div>
      <div className={`select-filter${isModuleFiltered ? ' select-filter--active' : ''}`}>
        <Select
          inlineLabelText={FILTER_LABEL_MODULE}
          options={selectModuleOptions}
          selectedAriaLabel="Selected"
          selectedOption={module}
          onChange={(event) => setModule(event.detail.selectedOption)}
          expandToViewport={true}
        />
      </div>
      <LiveRegion>{isFiltered && <span className="filtering-results">{getTextFilterCounterText(filteredItemsCount ?? 0)}</span>}</LiveRegion>
    </div>
  );

  return (
    <AppLayout
      ariaLabels={appLayoutAriaLabels}
      contentType="table"
      navigation={<HomePageSideNavigationV2 />}
      toolsHide={true}
      content={
        viewMode === 'list' ? (
          <Table
            {...collectionProps}
            enableKeyboardNavigation={true}
            columnDefinitions={COLUMN_DEFINITIONS}
            columnDisplay={preferences?.contentDisplay}
            items={items}
            variant="full-page"
            stickyHeader={true}
            resizableColumns={true}
            wrapLines={preferences?.wrapLines}
            stripedRows={preferences?.stripedRows}
            contentDensity={preferences?.contentDensity}
            renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) => `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`}
            header={headerContent}
            filter={filterContent}
          />
        ) : (
          <div>
            <div ref={stickyHeaderRef} className={`all-apps-grid-sticky-header${isScrolled ? ' all-apps-grid-sticky-header--scrolled' : ''}`}>
              {headerContent}
              {filterContent}
            </div>
            <AllAppsGridView items={items} />
          </div>
        )
      }
    />
  );
};

export default AllApps;
